import {
  Badge,
  Button,
  Card,
  Group,
  Menu,
  Modal,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useForm } from "@mantine/form";
import {
  IconCheck,
  IconChevronDown,
  IconExternalLink,
  IconPlus,
  IconSpy,
  IconUserPlus,
  IconUsers,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  createTenant,
  createTenantUser,
  getTenants,
  impersonateTenant,
  updateTenant,
} from "@/api/admin";
import { useAuthStore } from "@/stores/auth";
import { notify } from "@/lib/notify";
import {
  ListCell,
  ListEmpty,
  ListError,
  ListHeader,
  ListRow,
  ListScroll,
  ListSkeleton,
} from "@/components/DataList";
import { PageTitle } from "@/components/PageTitle";
import { EMPTY_LIST } from "@/lib/queryClient";

// Renk ve etiket tek yerde: ikisi ayrı durunca biri güncellenip diğeri unutuluyordu.
//
// `trial` BİLEREK yok: şema ve API enum'unda duruyor ama ürün böyle bir durum kullanmıyor, listeye
// koymak panelden girilebilir bir hâl uyduruyordu. Yine de gelirse aşağıdaki rozet ham değeri
// yazıyor — sessizce boş kalmasındansa panelin API'den geri kaldığı görünsün.
const TENANT_STATUSES = [
  { value: "active", label: "Aktif", color: "teal" },
  { value: "suspended", label: "Askıda", color: "red" },
];

const statusOf = (value) =>
  TENANT_STATUSES.find((status) => status.value === value);

const TENANT_COLUMNS = [
  { label: "Ad", flex: true },
  { label: "Menü", w: 150 },
  { label: "Durum", w: 150 },
  { label: "İşlem", w: 290 },
];

function TenantStatus({ tenant, pending, onChange }) {
  const current = statusOf(tenant.status);

  return (
    <Menu position="bottom-start" withinPortal shadow="md">
      <Menu.Target>
        <Badge
          component="button"
          type="button"
          variant="light"
          color={current?.color ?? "gray"}
          disabled={pending}
          rightSection={<IconChevronDown size={12} />}
          style={{ cursor: pending ? "progress" : "pointer", border: "none" }}
        >
          {/* Bilinmeyen durum ham değeriyle yazılıyor: sessizce boş bırakmak, panelin
                        API'den geri kaldığını gizlerdi. */}
          {current?.label ?? tenant.status}
        </Badge>
      </Menu.Target>
      <Menu.Dropdown>
        {TENANT_STATUSES.map((status) => (
          <Menu.Item
            key={status.value}
            leftSection={<Badge size="xs" circle color={status.color} />}
            rightSection={
              status.value === tenant.status ? <IconCheck size={14} /> : null
            }
            onClick={() =>
              status.value !== tenant.status && onChange(status.value)
            }
          >
            {status.label}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

function TenantFormModal({ opened, onClose }) {
  const queryClient = useQueryClient();

  const form = useForm({
    initialValues: { name: "", slug: "" },
    validate: {
      name: (value) => (value.trim() ? null : "Ad gerekli"),
      slug: (value) =>
        /^[a-z0-9]+(-[a-z0-9]+)*$/.test(value) ? null : "Geçersiz slug",
    },
  });

  const mutation = useMutation({
    mutationFn: (values) =>
      createTenant({ name: values.name.trim(), slug: values.slug.trim() }),
    onSuccess: () => {
      notify.success("Partner oluşturuldu");
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      form.reset();
      onClose();
    },
    onError: (error) =>
      notify.error(error.response?.data?.error ?? "Oluşturulamadı"),
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      radius="md"
      title={<Text fw={600}>Yeni Partner</Text>}
    >
      <form onSubmit={form.onSubmit((values) => mutation.mutate(values))}>
        <Stack gap="md">
          <TextInput
            label="Partner Adı"
            placeholder="Örn. Demo Kebap"
            {...form.getInputProps("name")}
          />
          <TextInput
            label="Subdomain"
            description="QR menü adresi: slug.noxussoft.com"
            placeholder="demo-kebap"
            {...form.getInputProps("slug")}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Vazgeç
            </Button>
            <Button
              type="submit"
              loading={mutation.isPending}
              leftSection={<IconPlus size={16} />}
            >
              Oluştur
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function TenantUserModal({ opened, onClose, tenant }) {
  const form = useForm({
    initialValues: { email: "", fullName: "" },
    validate: {
      email: (value) =>
        /^\S+@\S+\.\S+$/.test(value) ? null : "Geçerli e-posta girin",
      fullName: (value) => (value.trim() ? null : "Ad soyad gerekli"),
    },
  });

  const mutation = useMutation({
    mutationFn: (values) =>
      createTenantUser(tenant.id, { ...values, role: "owner" }),
    onSuccess: (created) => {
      // Hesap her hâlükârda açıldı; ayrım maildе. Mail gitmediyse "oluşturuldu" demek,
      // kimsenin giremeyeceği bir hesabı başarı gibi gösterirdi — davet elle iletilmeli.
      if (created?.invited) {
        notify.success(`Davet gönderildi: ${created.email}`);
      } else {
        notify.warning(
          `Kullanıcı açıldı ama davet maili gönderilemedi (${created?.email}). Yeniden ekleyerek tekrar deneyin.`,
          { autoClose: false },
        );
      }

      form.reset();
      onClose();
    },
    onError: (error) =>
      notify.error(error.response?.data?.error ?? "Kullanıcı oluşturulamadı"),
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      radius="md"
      title={<Text fw={600}>{tenant?.name} — kullanıcı ekle</Text>}
    >
      <form onSubmit={form.onSubmit((values) => mutation.mutate(values))}>
        <Stack gap="md">
          <TextInput
            label="E-posta"
            placeholder="ornek@firma.com"
            {...form.getInputProps("email")}
          />
          <TextInput
            label="Ad Soyad"
            placeholder="Ad Soyad"
            {...form.getInputProps("fullName")}
          />
          {/* Şifre alanı YOK: kullanıcı davet bağlantısıyla kendi şifresini belirliyor.
                        Buradan bir şifre üretip mail'e yazmak, partnerin fiyat ve entegrasyon
                        yetkisi taşıyan hesabının şifresini posta kutusunda süresiz bırakırdı. */}
          <Text size="sm" c="dimmed">
            Bu adrese bir davet bağlantısı gönderilecek; şifresini kendisi
            belirleyecek.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Vazgeç
            </Button>
            <Button
              type="submit"
              loading={mutation.isPending}
              leftSection={<IconUserPlus size={16} />}
            >
              Oluştur
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

export default function Tenants() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const impersonate = useAuthStore((state) => state.impersonate);
  const {
    data: tenants = EMPTY_LIST,
    isLoading,
    isError,
    refetch,
  } = useQuery({ queryKey: ["tenants"], queryFn: getTenants });

  const [formOpened, { open: openForm, close: closeForm }] =
    useDisclosure(false);
  const [userOpened, { open: openUser, close: closeUser }] =
    useDisclosure(false);
  const [selectedTenant, setSelectedTenant] = useState(null);

  const statusMutation = useMutation({
    mutationFn: ({ id, status }) => updateTenant(id, { status }),
    onSuccess: () => {
      notify.success("Partner durumu güncellendi");
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
    },
    onError: () => notify.error("Güncellenemedi"),
  });

  const impersonateMutation = useMutation({
    mutationFn: impersonateTenant,
    onSuccess: ({ token, tenant }) => {
      impersonate({ token, tenant });
      navigate("/");
    },
    onError: () => notify.error("Impersonation başarısız"),
  });

  return (
    <Stack gap="lg">
      <PageTitle>Partnerler</PageTitle>
      <Group justify="space-between">
        <Group>
          <ThemeIcon size={46} radius="md" variant="light" color="chocolate">
            <IconUsers size={26} stroke={1.6} />
          </ThemeIcon>
          <div>
            <Title order={2}>Partnerler</Title>
            <Text c="dimmed" size="sm">
              Tenant oluştur, durum yönet, kullanıcı ekle
            </Text>
          </div>
        </Group>
        <Button leftSection={<IconPlus size={16} />} onClick={openForm}>
          Yeni Partner
        </Button>
      </Group>

      <Card withBorder radius="md" p="md">
        {isError ? (
          <ListError title="Partnerler alınamadı" onRetry={refetch} />
        ) : isLoading ? (
          <Card withBorder radius="md" p={0}>
            <ListScroll minWidth={860}>
              <ListHeader columns={TENANT_COLUMNS} />
              <ListSkeleton columns={TENANT_COLUMNS} rows={5} />
            </ListScroll>
          </Card>
        ) : (
          <Card withBorder radius="md" p={0}>
            <ListScroll minWidth={860}>
              <ListHeader columns={TENANT_COLUMNS} />
              {tenants.map((tenant) => (
                <ListRow key={tenant.id}>
                  <ListCell flex>
                    <Text fw={500} truncate>
                      {tenant.name}
                    </Text>
                  </ListCell>
                  <ListCell w={150}>
                    {/* Etiket adresin kendisi DEĞİL yaptığı iş: tam alan adı
                                            220px kolona sığmıyordu ve "noxuschocolate.noxussoft."
                                            diye kelimenin ortasından kesiliyordu — okunmayan bir
                                            adres, adres olmuyor. Adres title'da duruyor. */}
                    <Button
                      component="a"
                      href={`https://${tenant.slug}.noxussoft.com`}
                      target="_blank"
                      rel="noreferrer"
                      title={`${tenant.slug}.noxussoft.com`}
                      size="compact-sm"
                      variant="light"
                      color="gray"
                      leftSection={<IconExternalLink size={12} />}
                    >
                      Menüyü Aç
                    </Button>
                  </ListCell>
                  <ListCell w={150}>
                    {/* Kutulu bir Select durumu FORM ALANI gibi gösteriyordu: satırdaki
                                            en ağır öğe en az önemli bilgiydi ve tablo tek bakışta
                                            taranamıyordu. Durgun hâli artık okunacak bir rozet, tıklanınca
                                            değiştirilebilir kalıyor. */}
                    <TenantStatus
                      tenant={tenant}
                      pending={
                        statusMutation.isPending &&
                        statusMutation.variables?.id === tenant.id
                      }
                      onChange={(status) =>
                        statusMutation.mutate({ id: tenant.id, status })
                      }
                    />
                  </ListCell>
                  <ListCell w={290} ta="right">
                    <Group gap="xs" justify="flex-end" wrap="nowrap">
                      <Button
                        size="compact-sm"
                        variant="default"
                        leftSection={<IconSpy size={14} />}
                        loading={
                          impersonateMutation.isPending &&
                          impersonateMutation.variables === tenant.id
                        }
                        onClick={() => impersonateMutation.mutate(tenant.id)}
                      >
                        Partner Gibi Gör
                      </Button>
                      <Button
                        size="compact-sm"
                        variant="light"
                        leftSection={<IconUserPlus size={14} />}
                        onClick={() => {
                          setSelectedTenant(tenant);
                          openUser();
                        }}
                      >
                        Kullanıcı Ekle
                      </Button>
                    </Group>
                  </ListCell>
                </ListRow>
              ))}
              {tenants.length === 0 && <ListEmpty>Partner yok</ListEmpty>}
            </ListScroll>
          </Card>
        )}
      </Card>

      <TenantFormModal opened={formOpened} onClose={closeForm} />
      <TenantUserModal
        opened={userOpened}
        onClose={closeUser}
        tenant={selectedTenant}
      />
    </Stack>
  );
}
